/**
 * Theme and layout settings — mode, skin, layout, primary color.
 *
 * This used to be a React context; it is a Zustand store now, so anything can
 * read or change a setting without a provider above it (route loaders, the
 * Storybook decorator, an event handler outside the tree).
 *
 * The starting point is `src/configs/theme.ts`. The cookie only takes part
 * when `userCustomizable` is on — see the Customizer.
 */
import { create } from 'zustand'

// Type Imports
import type { Layout, LayoutComponentWidth, Mode, Skin } from '@core/types'

// Config Imports
import theme from '@configs/theme'
import themeConfig from '@configs/theme-config'
import primaryColorConfig from '@configs/primary-color-config'

export type Settings = {
  mode?: Mode
  skin?: Skin
  semiDark?: boolean
  layout?: Layout
  navbarContentWidth?: LayoutComponentWidth
  contentWidth?: LayoutComponentWidth
  footerContentWidth?: LayoutComponentWidth
  primaryColor?: string
}

type UpdateOptions = { updateCookie?: boolean }

type SettingsStore = {
  settings: Settings
  /** Whether the current settings drifted from the theme file's defaults. */
  isSettingsChanged: boolean
  updateSettings: (settings: Partial<Settings>, options?: UpdateOptions) => void
  resetSettings: () => void
  /**
   * Applies settings that belong to a single page: they are not persisted, and
   * the returned function puts the previous ones back.
   *
   *   useEffect(() => updatePageSettings({ layout: 'collapsed' }), [])
   */
  updatePageSettings: (settings: Partial<Settings>) => () => void
}

const COOKIE_NAME = themeConfig.settingsCookieName

const readCookie = (): Settings => {
  if (!theme.userCustomizable || typeof document === 'undefined') return {}

  const escaped = COOKIE_NAME.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`))

  try {
    return match ? JSON.parse(decodeURIComponent(match[1])) : {}
  } catch {
    return {}
  }
}

const writeCookie = (settings: Settings) => {
  if (!theme.userCustomizable || typeof document === 'undefined') return

  const value = encodeURIComponent(JSON.stringify(settings))

  document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`
}

/** What `src/configs/theme.ts` asks for — the baseline `resetSettings` returns to. */
const defaultSettings: Settings = {
  mode: themeConfig.mode,
  skin: themeConfig.skin,
  semiDark: themeConfig.semiDark,
  layout: themeConfig.layout,
  navbarContentWidth: themeConfig.navbar.contentWidth,
  contentWidth: themeConfig.contentWidth,
  footerContentWidth: themeConfig.footer.contentWidth,
  primaryColor: primaryColorConfig[0].main
}

const changed = (settings: Settings) => JSON.stringify(settings) !== JSON.stringify(defaultSettings)

export const useSettings = create<SettingsStore>((set, get) => {
  /** Settings the page-scoped ones revert to. */
  let persisted: Settings = { ...defaultSettings, ...readCookie() }

  return {
    settings: persisted,
    isSettingsChanged: changed(persisted),

    updateSettings(settings, options) {
      const { updateCookie = true } = options ?? {}
      const next = { ...get().settings, ...settings }

      if (updateCookie) {
        persisted = next
        writeCookie(next)
      }

      set({ settings: next, isSettingsChanged: changed(next) })
    },

    resetSettings() {
      get().updateSettings(defaultSettings)
    },

    updatePageSettings(settings) {
      get().updateSettings(settings, { updateCookie: false })

      return () => get().updateSettings(persisted, { updateCookie: false })
    }
  }
})

/** The effective mode, resolving `'system'` against the operating system. */
export const systemMode = (): Exclude<Mode, 'system'> => {
  const { mode } = useSettings.getState().settings

  if (mode !== 'system') return mode ?? 'light'

  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}
